import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getFeedbackByUuid } from 'Shared/selectors/Feedbacks.selectors';
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';
import { fetchSingleFeedback } from 'Shared/actions/Feedbacks.actions';
import { FeedbackRecognitionTemplateTypes } from 'Shared/constants/types';
import Spinner from 'Shared/components/Spinner';
import { WpModal } from 'Wpds/components/modal';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import FeedbackBlankTemplate from './FeedbackBlankTemplate/FeedbackBlankTemplate';
import FeedbackStrengthsOpportunitiesTemplate from './FeedbackStrengthsOpportunitiesTemplate/FeedbackStrengthsOpportunitiesTemplate';
import FeedbackGPT3coach from './FeedbackAutoCoachTemplate/FeedbackGPT3coach';
import FeedbackFromReviewTemplateTemplate from './FeedbackFromReviewTemplateTemplate/FeedbackFromReviewTemplateTemplate';

import './FeedbackForm.scss';

type Props = {
  choosenTemplate: FeedbackRecognitionTemplateTypes;
};

const prefilledTypes = [
  FeedbackRecognitionTemplateTypes.feedbackLikeThatWishThatTemplate,
  FeedbackRecognitionTemplateTypes.feedbackStrengthsOpportunitiesTemplate,
];

function FeedbackForm({ choosenTemplate }: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const params = useParams();
  const { feedbackUuid, workspaceId } = params;

  const { pathname, search } = useLocation();
  const isEditFeedbackPage = pathname.includes('edit');
  const { review_template_slug: reviewTemplateSlugFromParams } = queryString.parse(search);

  const blankFeedbackTemplateInstructions = useSelector(state => state.organization?.blankFeedbackTemplateInstructions);
  const { id: currentUserId } = useCurrentUser();
  const feedback = useDeepEqualSelector(getFeedbackByUuid(feedbackUuid));
  const templateType = feedback?.templateType;
  const feedbackCreatorId = feedback?.creatorId;
  const reviewTemplateSlug = feedback?.reviewTemplateSlug || reviewTemplateSlugFromParams;

  useEffect(() => {
    async function fetchFeedback() {
      setIsLoading(true);
      await dispatch(fetchSingleFeedback(feedbackUuid));
      setIsLoading(false);
    }
    if (isEditFeedbackPage) fetchFeedback();
  }, []);

  useEffect(() => {
    if (isEditFeedbackPage && feedbackCreatorId && feedbackCreatorId !== currentUserId) {
      history.push(PageUrlsUtil.makeWorkspaceFeedbackUrl(workspaceId));
    }
  }, [feedbackCreatorId]);

  const isGtp3Template =
    templateType === FeedbackRecognitionTemplateTypes.feedbackGPT3Coach ||
    choosenTemplate === FeedbackRecognitionTemplateTypes.feedbackGPT3Coach;

  const isBlankFeedbackTemplate =
    templateType === FeedbackRecognitionTemplateTypes.feedbackBlankTemplate ||
    choosenTemplate === FeedbackRecognitionTemplateTypes.feedbackBlankTemplate;

  const isFromReviewTemplateFeedbackTemplate =
    templateType === FeedbackRecognitionTemplateTypes.feedbackfromReviewTemplateTemplate ||
    choosenTemplate === FeedbackRecognitionTemplateTypes.feedbackfromReviewTemplateTemplate;

  const isStrengthsOpportunitiesFeedbackTemplate =
    prefilledTypes.includes(templateType) || prefilledTypes.includes(choosenTemplate);

  return (
    <WpModal isOpen size="full">
      <div className="FeedbackForm">
        {isLoading ? (
          <Spinner activeIf className="mt-5" />
        ) : (
          <>
            {isBlankFeedbackTemplate && <FeedbackBlankTemplate instructions={blankFeedbackTemplateInstructions} />}
            {isStrengthsOpportunitiesFeedbackTemplate && <FeedbackStrengthsOpportunitiesTemplate />}
            {isGtp3Template && <FeedbackGPT3coach />}
            {isFromReviewTemplateFeedbackTemplate && (
              <FeedbackFromReviewTemplateTemplate reviewTemplateSlug={reviewTemplateSlug} />
            )}
          </>
        )}
      </div>
    </WpModal>
  );
}

export default FeedbackForm;
